import { toast } from '@heidi/design-system/toast';
import type { ModalId } from '@heidi/schema/ml-scribe';
import { useQueuedModal } from '@heidi/utils';
import {
  useMutateDeclineTeamInvitation,
  useQueryPendingTeamInvitations,
} from '@heidi/workspace/data';
import { useIntl } from 'react-intl';
import { JoinWorkspaceDialog } from '../../join-workspace-dialog/join-workspace-dialog';
import type { JoinWorkspaceDialogPorts } from '../../join-workspace-dialog/ports';
import { pendingTeamInviteModalMessages } from './pending-team-invite-modal.messages';

/**
 * Prompts a user who signed up without opening their invite link. Ignoring declines,
 * deleting the invitation so the user falls through to normal product onboarding.
 */
export function PendingTeamInviteModal({
  modalId,
  joinDialogPorts,
}: {
  modalId: ModalId;
  joinDialogPorts: JoinWorkspaceDialogPorts;
}) {
  const intl = useIntl();
  const { isOpen } = useQueuedModal(modalId);
  const { data: invitationsData } = useQueryPendingTeamInvitations();
  const { mutate: declineInvite } = useMutateDeclineTeamInvitation();

  const invitation = invitationsData?.pending_invitations[0];

  if (!isOpen || !invitation) {
    return null;
  }

  return (
    <JoinWorkspaceDialog
      invitation={{
        team_id: invitation.team_id,
        workspace_name: invitation.team_name,
      }}
      dismissible={false}
      onIgnore={() =>
        declineInvite(
          { teamId: invitation.team_id },
          {
            // The prompt is non-dismissible and only stands down once the
            // invitation is gone, so a silent failure would strand the user.
            onError: () =>
              toast({
                tone: 'destructive',
                title: intl.formatMessage(pendingTeamInviteModalMessages.declineFailed),
              }),
          }
        )
      }
      onClose={() => undefined}
      ports={joinDialogPorts}
    />
  );
}
